import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import { scheduleDelayedRefetch } from '../graphql/util/scheduleDelayedRefetch'
import type { Task } from '@paintscout/api'
import {
  updateCachedTask,
  updateCachedTaskPartially,
  useDeleteTasksMutation,
  useSaveTaskMutation
} from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'

const DEFAULT_REFETCH_QUERIES = ['searchActivities']

/**
 * Returns common useMutations for saving tasks and handles updating the caches
 */
export function useTaskMutations() {
  const [saveTaskMutation] = useSaveTaskMutation()
  const [deleteTasksMutation] = useDeleteTasksMutation()
  const client = useApolloClient()

  return useMemo(
    () => ({
      async saveTask({ task, skipActivity }: { task: Task; skipActivity?: boolean }) {
        const timeOfSave = Date.now()
        const mutationId = `save-task-${task._id}`
        const result = await saveTaskMutation({
          variables: {
            task,
            skipActivity
          },
          context: {
            __mutation_id__: mutationId
          },

          optimisticResponse: {
            saveTask: {
              __typename: 'Task' as const,
              created: null,
              ...task,
              owner: task.owner
                ? {
                    __typename: 'Owner',
                    ...task.owner
                  }
                : null,
              _rev: task._rev || null // null is important, if its undefined it will break cache
            }
          },
          update(cache: ApolloCache, { data }) {
            if (data?.saveTask) {
              updateCachedTask(cache, data.saveTask)
            }
          }
        })

        removeStoredMutationsById(client, mutationId, { before: timeOfSave })

        if (!skipActivity) {
          scheduleDelayedRefetch(client, DEFAULT_REFETCH_QUERIES)
        }

        return {
          task: result.data.saveTask
        }
      },

      async deleteTasks(ids: string[]) {
        const result = await deleteTasksMutation({
          variables: { ids },
          optimisticResponse: {
            deleteTasks: ids.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              // we can fake the task document as just an object with _deleted flag
              updateCachedTaskPartially(cache, id, {
                _deleted: true
              })
            })
          }
        })

        scheduleDelayedRefetch(client, DEFAULT_REFETCH_QUERIES)

        return result
      }
    }),
    [client]
  )
}
